// Analytics preferences persistence service using localStorage
// CREATED: Manage user-specific filter preferences for Analytics page
// Features: Save/restore site, mission, date range, and time slot selections
// TTL: 7 days to avoid restoring stale preferences
// Format: tasq_analytics_prefs_{userId}
// UPDATED: Added lastTimeSlot field for scheduled time filter persistence

const CACHE_VERSION = '1.1';
const PREFERENCES_TTL = 7 * 24 * 60 * 60 * 1000;

export interface AnalyticsPreferences {
  lastSiteId: string | null;
  lastMissionId: string | null;
  lastDateFrom: string | null;
  lastDateTo: string | null;
  lastTimeSlot: string | null;
}

interface PreferencesCacheEntry {
  data: AnalyticsPreferences;
  timestamp: number;
  version: string;
}

function getPreferencesKey(userId: string): string {
  return `tasq_analytics_prefs_${userId}`;
}

function isValidCache(entry: PreferencesCacheEntry | null): entry is PreferencesCacheEntry {
  if (!entry) return false;
  if (entry.version !== CACHE_VERSION) return false;
  return (Date.now() - entry.timestamp) < PREFERENCES_TTL;
}

export function saveAnalyticsPreferences(userId: string, preferences: AnalyticsPreferences): void {
  try {
    const key = getPreferencesKey(userId);
    const entry: PreferencesCacheEntry = {
      data: preferences,
      timestamp: Date.now(),
      version: CACHE_VERSION
    };
    localStorage.setItem(key, JSON.stringify(entry));
  } catch (_error) {}
}

export function getAnalyticsPreferences(userId: string): AnalyticsPreferences | null {
  try {
    const key = getPreferencesKey(userId);
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    const entry: PreferencesCacheEntry = JSON.parse(cached);
    if (!isValidCache(entry)) {
      localStorage.removeItem(key);
      return null;
    }
    return { lastTimeSlot: null, ...entry.data };
  } catch (_error) {
    return null;
  }
}

export function clearAnalyticsPreferences(userId: string): void {
  try {
    localStorage.removeItem(getPreferencesKey(userId));
  } catch (_error) {}
}
